import { useEffect, useMemo, useState } from "react";
import { FiArrowLeft, FiArrowRight, FiCheck } from "react-icons/fi";
import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";
import {
	useRoles,
	useRoleTemplates,
	useAssignmentMutations,
} from "newui/features/RolesPermissions/hooks/useUserAssignments";
import { useScopeOptions } from "newui/features/RolesPermissions/hooks/useScopeOptions";
import type {
	AssignRolePayload,
	PermissionGridEntry,
	ScopeOption,
	UserAssignment,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { Step1UserRolePicker, AssignMode } from "./Step1UserRolePicker";
import { Step2ScopeSearch, scopeKey } from "./Step2ScopeSearch";

interface AssignWizardProps {
	isOpen: boolean;
	onClose: () => void;
	users: UserAssignment[];
	// Pre-selected user ids when the wizard opens (e.g. jumping in from a single
	// user's "Manage roles" drawer). Empty/omitted = start with no selection.
	initialUserIds?: number[];
	// PRO-732a: opened from a single user's "Manage roles" drawer, scoping THAT
	// user's role to projects/RFx events — Step 1 shows who's being edited instead
	// of the bulk search/pick-list/chips UI. Explicit (not derived from
	// `initialUserIds.length === 1`): a bulk flow can legitimately start with one
	// user preselected, so only the caller (which entry point was used) can tell
	// these apart.
	singleUserMode?: boolean;
	// Optional read-only permission grid for the chosen role (server-authoritative levels).
	permissionGridForRole?: (roleId: number | null) => PermissionGridEntry[];
	onCompleted?: () => void;
	/**
	 * PRO-730 — the "Allocate roles to several users" guided tour needs to
	 * point at `wizard-confirm`, which only renders on Step 2, without ever
	 * actually selecting users/a role (that would risk a real submission).
	 * Applied whenever it changes while the wizard is open, so a caller can
	 * drive `step` externally for a tour without otherwise controlling it.
	 * Omit for normal use — Step 1/2 navigation is entirely internal then.
	 */
	forcedStep?: 1 | 2;
}

const isGlobalOption = (option: ScopeOption): boolean => option.scope_type === "global";

// C16: "give user role X on scope Y" is a scoped ASSIGNMENT ROW (POST /roles with scope_type/scope_id
// verbatim from the catalogue) — NOT a global assignment narrowed by grants (which would over-grant,
// leaving full access everywhere the grants don't cover). Scope grants are ceilings only and never
// appear in this flow. Level comes from the role matrix server-side — the UI carries no level.
export const buildRoleAssignmentPayloads = (
	roleId: number,
	selectedScopes: ScopeOption[]
): AssignRolePayload[] => {
	const concrete = selectedScopes.filter(
		(option): option is ScopeOption & { scope_id: number } =>
			!isGlobalOption(option) && option.scope_id !== null
	);

	// No concrete pick (or the synthetic "Company-wide" pick) → a single global assignment row.
	if (selectedScopes.some(isGlobalOption) || concrete.length === 0) {
		return [{ role_id: roleId, scope_type: "global" }];
	}

	// One scoped assignment row per pick — identity taken verbatim from the catalogue option.
	return concrete.map((option) => ({
		role_id: roleId,
		scope_type: option.scope_type,
		scope_id: option.scope_id,
	}));
};

// 2-step multi-user assign wizard (supersedes the rejected ScopeTreeAssigner cascade tree).
export const AssignWizard = ({
	isOpen,
	onClose,
	users,
	initialUserIds,
	singleUserMode = false,
	permissionGridForRole,
	onCompleted,
	forcedStep,
}: AssignWizardProps) => {
	const { roles } = useRoles();
	const { templates } = useRoleTemplates();
	const { assignRoleMutation, assignTemplateMutation } = useAssignmentMutations();

	const [step, setStep] = useState<1 | 2>(1);
	const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
	const [mode, setMode] = useState<AssignMode>("template");
	const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
	const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
	// F3: store the full ScopeOption objects (NOT keys resolved against the live search page), so a
	// pick survives changing the query — chips and the submit payload build from these, never the
	// current (capped, query-driven) results page.
	const [selectedScopes, setSelectedScopes] = useState<ScopeOption[]>([]);
	const [scopeQuery, setScopeQuery] = useState("");
	const [error, setError] = useState<string | null>(null);

	// Seed the user selection whenever the wizard opens. The parent passes a stable
	// state array, so this only re-runs on open (or when the seed itself changes) —
	// it does not fight the user's manual toggles while the wizard is open.
	useEffect(() => {
		if (isOpen) {
			setSelectedUserIds(initialUserIds ?? []);
		}
	}, [isOpen, initialUserIds]);

	// PRO-730 — lets a guided tour drive Step 1 -> Step 2 without touching the
	// user/role selection. No-op for every other caller (forcedStep is undefined).
	useEffect(() => {
		if (isOpen && forcedStep !== undefined) {
			setStep(forcedStep);
		}
	}, [isOpen, forcedStep]);

	// Template mode is company-wide only (server hardcodes Global) — no scope catalogue needed.
	const scopeEnabled = isOpen && step === 2 && mode === "role";
	const { scopeOptions, isLoading: scopeLoading } = useScopeOptions(
		{ q: scopeQuery || undefined },
		scopeEnabled
	);

	const reset = () => {
		setStep(1);
		setSelectedUserIds([]);
		setMode("template");
		setSelectedRoleId(null);
		setSelectedTemplateId(null);
		setSelectedScopes([]);
		setScopeQuery("");
		setError(null);
	};

	const handleClose = () => {
		reset();
		onClose();
	};

	const toggleUser = (userId: number) => {
		setSelectedUserIds((prev) =>
			prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
		);
	};

	// Bulk-set selection for a list of user ids — drives the picker's filter-aware
	// select-all (add/remove exactly the currently-matching users, leaving the rest).
	const setUsersSelected = (ids: number[], selected: boolean) => {
		setSelectedUserIds((prev) => {
			const next = new Set(prev);
			ids.forEach((id) => (selected ? next.add(id) : next.delete(id)));
			return Array.from(next);
		});
	};

	// F4: the synthetic "Company-wide" (global) pick is mutually exclusive with concrete scopes.
	const toggleScope = (option: ScopeOption) => {
		const key = scopeKey(option.scope_type, option.scope_id);
		setSelectedScopes((prev) => {
			const already = prev.some((s) => scopeKey(s.scope_type, s.scope_id) === key);
			if (isGlobalOption(option)) {
				return already ? [] : [option];
			}
			const withoutGlobal = prev.filter((s) => !isGlobalOption(s));
			return already
				? withoutGlobal.filter((s) => scopeKey(s.scope_type, s.scope_id) !== key)
				: [...withoutGlobal, option];
		});
	};

	const removeScope = (option: ScopeOption) => {
		const key = scopeKey(option.scope_type, option.scope_id);
		setSelectedScopes((prev) =>
			prev.filter((s) => scopeKey(s.scope_type, s.scope_id) !== key)
		);
	};

	const step1Valid =
		selectedUserIds.length > 0 &&
		(mode === "template" ? selectedTemplateId !== null : selectedRoleId !== null);

	const roleName = useMemo(
		() => roles.find((role) => role.id === selectedRoleId)?.name ?? null,
		[roles, selectedRoleId]
	);

	const permissionGrid = permissionGridForRole ? permissionGridForRole(selectedRoleId) : [];

	const isSubmitting = assignRoleMutation.isPending || assignTemplateMutation.isPending;

	// Applies the chosen role/template to ALL Step-1 users in one action. Role mode fans out one
	// scoped assignment POST per (user × pick); template mode is a single company-wide POST per user.
	const handleConfirm = async () => {
		setError(null);
		try {
			if (mode === "template" && selectedTemplateId !== null) {
				await Promise.all(
					selectedUserIds.map((userId) =>
						assignTemplateMutation.mutateAsync({ userId, templateId: selectedTemplateId })
					)
				);
			} else if (mode === "role" && selectedRoleId !== null) {
				const payloads = buildRoleAssignmentPayloads(selectedRoleId, selectedScopes);
				await Promise.all(
					selectedUserIds.map((userId) =>
						Promise.all(
							payloads.map((payload) =>
								assignRoleMutation.mutateAsync({ userId, payload })
							)
						)
					)
				);
			}
			onCompleted?.();
			handleClose();
		} catch (submitError) {
			// C13: a genuine gate/validation denial (403/422) surfaces here — never client-clamped.
			const message =
				submitError instanceof Error
					? submitError.message
					: "Assignment failed. Some changes may not have been applied.";
			setError(message);
		}
	};

	const footer = (
		<div className="flex w-full items-center justify-between">
			<div>
				{step === 2 && (
					<Button
						btntype="secondary"
						size="sm"
						onClick={() => setStep(1)}
						data-testid="wizard-back"
					>
						<FiArrowLeft className="mr-1 inline" size={14} /> Back
					</Button>
				)}
			</div>
			<div className="flex gap-2">
				<Button btntype="plain-border" size="sm" onClick={handleClose} data-testid="wizard-cancel">
					Cancel
				</Button>
				{step === 1 ? (
					<Button
						btntype="primary"
						size="sm"
						disabled={!step1Valid}
						onClick={() => setStep(2)}
						data-testid="wizard-next"
					>
						Next <FiArrowRight className="ml-1 inline" size={14} />
					</Button>
				) : (
					<Button
						btntype="primary"
						size="sm"
						isLoading={isSubmitting}
						disabled={isSubmitting}
						onClick={handleConfirm}
						data-testid="wizard-confirm"
					>
						<FiCheck className="mr-1 inline" size={14} /> Confirm assignment
					</Button>
				)}
			</div>
		</div>
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title={step === 1 ? "Assign role or template" : "Assign scope"}
			size="xxl"
			modalType="standard"
			customFooter={footer}
			// PRO-728: neither step's list uses `position: sticky` (unlike the access
			// matrix table), so this is the "prose/list" case — the inner caps below
			// are removed and the modal body (already scrollable via
			// scrollBehavior="inside") is the single scroll owner. No `fillBody` needed.
		>
			<div data-testid="assign-wizard">
				{error && (
					<div
						className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
						data-testid="wizard-error"
					>
						{error}
					</div>
				)}
				{step === 1 ? (
					<Step1UserRolePicker
						users={users}
						selectedUserIds={selectedUserIds}
						onToggleUser={toggleUser}
						onSetUsersSelected={setUsersSelected}
						mode={mode}
						onModeChange={setMode}
						roles={roles}
						selectedRoleId={selectedRoleId}
						onSelectRole={setSelectedRoleId}
						templates={templates}
						selectedTemplateId={selectedTemplateId}
						onSelectTemplate={setSelectedTemplateId}
						singleUserMode={singleUserMode}
					/>
				) : (
					<Step2ScopeSearch
						scopeOptions={scopeOptions}
						selectedScopes={selectedScopes}
						onToggleScope={toggleScope}
						onRemoveScope={removeScope}
						roleName={roleName}
						permissionGrid={permissionGrid}
						targetUserCount={selectedUserIds.length}
						isLoading={scopeLoading}
						onQueryChange={setScopeQuery}
						// Template mode is company-wide only — no scope picking (server hardcodes Global).
						scopeSelectionDisabled={mode === "template"}
					/>
				)}
			</div>
		</Modal>
	);
};
